export const freightBookingSteps = [
  "route",
  "freight",
  "quotes",
  "book",
] as const;
export type FreightBookingStep = (typeof freightBookingSteps)[number];

export const freightBookingStepLabels: Record<FreightBookingStep, string> = {
  route: "Route",
  freight: "Freight",
  quotes: "Quotes",
  book: "Book",
};

import {
  FreightClass,
  FreightLocationType,
  HandlingUnitType,
} from "@redotech/redo-fulfillment-carrier-common/freight";
import type { FulfillmentOrderAddress } from "@redotech/redo-fulfillment-customs-common/fulfillment-order-address";
import type {
  FreightDeliveryAccessorialPresetId,
  FreightHandlingUnitId,
  FreightPickupAccessorialPresetId,
  FreightPickupWindowId,
} from "@redotech/redo-fulfillment-id";
import { Weight } from "@redotech/redo-fulfillment-outbound-labels-common/util";
import type { FreightDeliveryAccessorialPresetRow } from "@redotech/redo-model/freight/freight-delivery-accessorial-preset";
import type { FreightHandlingUnitRow } from "@redotech/redo-model/freight/freight-handling-unit";
import type { FreightPickupAccessorialPresetRow } from "@redotech/redo-model/freight/freight-pickup-accessorial-preset";
import type { FreightPickupWindowRow } from "@redotech/redo-model/freight/freight-pickup-window";
import type {
  DeliveryAccessorials,
  FreightDimensions,
  FreightQuote,
  PickupAccessorials,
} from "@redotech/redo-model/freight/freight-quote-request";

export interface FreightProduct {
  id: string;
  name: string;
  sku: string | undefined;
  itemWeight: Weight;
  quantity: number;
  dimensions?: FreightDimensions;
}

export const CUSTOM_PICKUP_WINDOW_ID = "__custom_pickup_window__";
export const CUSTOM_PICKUP_ACCESSORIAL_ID = "__custom_pickup_accessorial__";
export const CUSTOM_DELIVERY_ACCESSORIAL_ID = "__custom_delivery_accessorial__";

export type PickupWindowSelection =
  | { type: "saved"; id: FreightPickupWindowId }
  | { type: "custom"; id: typeof CUSTOM_PICKUP_WINDOW_ID };

export type PickupAccessorialSelection =
  | { type: "saved"; id: FreightPickupAccessorialPresetId }
  | { type: "custom"; id: typeof CUSTOM_PICKUP_ACCESSORIAL_ID };

export type DeliveryAccessorialSelection =
  | { type: "saved"; id: FreightDeliveryAccessorialPresetId }
  | { type: "custom"; id: typeof CUSTOM_DELIVERY_ACCESSORIAL_ID };

export interface RouteStepData {
  pickupDateTime: Temporal.PlainDateTime | undefined;
  closeTime: Temporal.PlainTime | undefined;
  pickupWindowSelection: PickupWindowSelection | undefined;
  pickupLocationType: FreightLocationType | undefined;
  pickupAccessorialSelection: PickupAccessorialSelection | undefined;
  pickupAccessorials: PickupAccessorials;
  deliveryLocationType: FreightLocationType | undefined;
  deliveryAccessorialSelection: DeliveryAccessorialSelection | undefined;
  deliveryAccessorials: DeliveryAccessorials;
}

export function validateAddress(
  address: FulfillmentOrderAddress,
  label: string,
): string | undefined {
  if (!address.address1) {
    return `${label} is missing a street address`;
  }
  if (!address.city) {
    return `${label} is missing a city`;
  }
  if (!address.province) {
    return `${label} is missing a state/province`;
  }
  if (!address.zip) {
    return `${label} is missing a postal code`;
  }
  if (!address.countryCode) {
    return `${label} is missing a country`;
  }
  if (address.countryCode !== "US") {
    return `${label} must be a US address`;
  }
  if (!address.name) {
    return `${label} is missing a contact name`;
  }
  if (!address.phone) {
    return `${label} is missing a phone number`;
  }
  return undefined;
}

export function validateRouteStep(
  data: RouteStepData,
  originAddress: FulfillmentOrderAddress,
  destinationAddress: FulfillmentOrderAddress,
): string | undefined {
  const originError = validateAddress(originAddress, "Origin address");
  if (originError) {
    return originError;
  }
  const destinationError = validateAddress(
    destinationAddress,
    "Destination address",
  );
  if (destinationError) {
    return destinationError;
  }
  if (data.pickupDateTime === undefined) {
    return "Please select a pickup date";
  }
  const dayOfWeek = data.pickupDateTime.toPlainDate().dayOfWeek;
  if (dayOfWeek === 6 || dayOfWeek === 7) {
    return "Pickup date cannot be on a weekend";
  }
  if (data.pickupWindowSelection === undefined) {
    return "Please select a pickup window";
  }
  if (data.closeTime === undefined) {
    return "Please select a close time";
  }
  const readyTime = data.pickupDateTime.toPlainTime();
  if (Temporal.PlainTime.compare(data.closeTime, readyTime) < 0) {
    return "Close time must not be before the pickup ready time";
  }
  return undefined;
}

export function validateFreightStep(data: FreightStepData): string | undefined {
  if (data.products.length === 0) {
    return "No products available for freight booking";
  }
  for (const product of data.products) {
    if (product.itemWeight.value <= 0) {
      return `${product.name} has an invalid weight`;
    }
  }

  for (const unit of data.handlingUnits) {
    if (
      !unit.dimensions ||
      unit.dimensions.length <= 0 ||
      unit.dimensions.width <= 0 ||
      unit.dimensions.height <= 0
    ) {
      return "All handling units must have dimensions set";
    }
    if (!unit.freightClass) {
      return "All handling units must have a freight class";
    }
  }

  const assignedPerProduct = new Map<string, number>();
  for (const unit of data.handlingUnits) {
    for (const ap of unit.assignedProducts) {
      assignedPerProduct.set(
        ap.productId,
        (assignedPerProduct.get(ap.productId) ?? 0) + ap.quantity,
      );
    }
  }
  for (const product of data.products) {
    const assigned = assignedPerProduct.get(product.id) ?? 0;
    if (assigned < product.quantity) {
      return "All product quantities must be assigned to a handling unit";
    }
  }

  return undefined;
}

export interface AssignedProduct {
  productId: string;
  quantity: number;
}

export const CUSTOM_HANDLING_UNIT_ID = "__custom_handling_unit__";

export type HandlingUnitPreset =
  | { type: "saved"; id: FreightHandlingUnitId }
  | { type: "custom"; id: typeof CUSTOM_HANDLING_UNIT_ID };

export interface HandlingUnit {
  preset: HandlingUnitPreset;
  type: HandlingUnitType;
  quantity: number;
  stackable: boolean;
  dimensions?: FreightDimensions;
  freightClass?: FreightClass;
  assignedProducts: AssignedProduct[];
}

export function createHandlingUnit(
  savedUnit?: FreightHandlingUnitRow,
): HandlingUnit {
  if (savedUnit) {
    return {
      preset: { type: "saved", id: savedUnit.id },
      type: savedUnit.type,
      quantity: 1,
      stackable: false,
      dimensions: {
        length: savedUnit.length,
        width: savedUnit.width,
        height: savedUnit.height,
      },
      assignedProducts: [],
    };
  }
  return {
    preset: { type: "custom", id: CUSTOM_HANDLING_UNIT_ID },
    type: HandlingUnitType.PALLET,
    quantity: 1,
    stackable: false,
    assignedProducts: [],
  };
}

export interface FreightStepData {
  products: FreightProduct[];
  handlingUnits: HandlingUnit[];
  customerReference1: string;
  customerReference2: string;
  specialInstructions: string;
}

export interface FreightBookingState {
  route: RouteStepData;
  freight: FreightStepData;
  selectedQuote: FreightQuote | undefined;
}

function mergeProducts(freightProducts: FreightProduct[]): FreightProduct[] {
  const byId = new Map<string, FreightProduct>();
  for (const p of freightProducts) {
    const existing = byId.get(p.id);
    if (existing) {
      byId.set(p.id, { ...existing, quantity: existing.quantity + p.quantity });
    } else {
      byId.set(p.id, { ...p });
    }
  }
  return [...byId.values()];
}

export function createInitialFreightBookingState(
  freightProducts: FreightProduct[],
  pickupWindows: FreightPickupWindowRow[],
  pickupAccessorialPresets: FreightPickupAccessorialPresetRow[],
  deliveryAccessorialPresets: FreightDeliveryAccessorialPresetRow[],
  originAddress: FulfillmentOrderAddress,
  destinationAddress: FulfillmentOrderAddress,
  customerReference1?: string,
): FreightBookingState {
  const defaultWindow = pickupWindows.find((w) => w.isDefault);
  const defaultPickupAccessorial = pickupAccessorialPresets.find(
    (p) => p.isDefault,
  );
  const defaultDeliveryAccessorial = deliveryAccessorialPresets.find(
    (p) => p.isDefault,
  );
  const originResidential = originAddress.residential === true;
  const destinationResidential = destinationAddress.residential === true;
  return {
    freight: {
      products: mergeProducts(freightProducts),
      handlingUnits: [],
      customerReference1: customerReference1?.slice(0, 20) ?? "",
      customerReference2: "",
      specialInstructions: "",
    },
    selectedQuote: undefined,
    route: {
      pickupDateTime: undefined,
      closeTime: defaultWindow?.pickupCloseTime,
      pickupWindowSelection: defaultWindow
        ? { type: "saved", id: defaultWindow.id }
        : undefined,
      pickupLocationType: originResidential
        ? FreightLocationType.RESIDENTIAL
        : undefined,
      pickupAccessorialSelection: defaultPickupAccessorial
        ? { type: "saved", id: defaultPickupAccessorial.id }
        : { type: "custom", id: CUSTOM_PICKUP_ACCESSORIAL_ID },
      pickupAccessorials: defaultPickupAccessorial
        ? {
            liftgateRequired:
              defaultPickupAccessorial.liftgateRequired || originResidential,
            insidePickup: defaultPickupAccessorial.insidePickup,
            instructions: defaultPickupAccessorial.instructions ?? "",
          }
        : {
            liftgateRequired: originResidential,
            insidePickup: false,
            instructions: "",
          },
      deliveryLocationType: destinationResidential
        ? FreightLocationType.RESIDENTIAL
        : undefined,
      deliveryAccessorialSelection: defaultDeliveryAccessorial
        ? { type: "saved", id: defaultDeliveryAccessorial.id }
        : { type: "custom", id: CUSTOM_DELIVERY_ACCESSORIAL_ID },
      deliveryAccessorials: defaultDeliveryAccessorial
        ? {
            liftgateRequired:
              defaultDeliveryAccessorial.liftgateRequired ||
              destinationResidential,
            insideDelivery: defaultDeliveryAccessorial.insideDelivery,
            appointmentRequired: defaultDeliveryAccessorial.appointmentRequired,
            instructions: defaultDeliveryAccessorial.instructions ?? "",
          }
        : {
            liftgateRequired: destinationResidential,
            insideDelivery: false,
            appointmentRequired: false,
            instructions: "",
          },
    },
  };
}

// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export {
  type DeliveryAccessorials,
  type FreightDimensions,
  type PickupAccessorials,
} from "@redotech/redo-model/freight/freight-quote-request";
